import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { routes } from '@/lib/routes';
import { getPreliminaryAnswers, getWorkRequests } from '../api';
import { WorkRequestsRegistry } from './WorkRequestsRegistry';
import { PreliminaryRegistryView } from './PreliminaryRegistryView';
import { RequestPassportModal } from './RequestPassportModal';
import logo from '@/assets/logo.svg';

interface WorkRequestsHubProps {
  roleContext?: string;
  basePath?: string;
  initialTab?: 'registry' | 'preliminary';
}

function tabFromPath(pathname: string): 'registry' | 'preliminary' {
  if (pathname.endsWith('/preliminary')) return 'preliminary';
  return 'registry';
}

export const WorkRequestsHub: React.FC<WorkRequestsHubProps> = ({
  roleContext = 'SYSTEM_OWNER',
  initialTab = 'registry',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'registry' | 'preliminary'>(
    tabFromPath(location.pathname) || initialTab,
  );
  const [reqCount, setReqCount] = useState(0);
  const [preCount, setPreCount] = useState(0);
  const [selectedReqCode, setSelectedReqCode] = useState<string | null>(null);

  useEffect(() => {
    setActiveTab(tabFromPath(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    const fetchCounters = async () => {
      try {
        const [reqs, pres] = await Promise.all([
          getWorkRequests({ role_context: roleContext }),
          getPreliminaryAnswers({ role_context: roleContext }),
        ]);
        if (reqs?.counts?.total !== undefined) setReqCount(reqs.counts.total);
        if (pres) {
          setPreCount(pres.filter((p) => p.status === 'NEEDS_CONFIRMATION').length);
        }
      } catch {
        /* keep zeros */
      }
    };
    void fetchCounters();
  }, [roleContext]);

  const changeTab = (id: 'registry' | 'preliminary') => {
    setActiveTab(id);
    navigate(id === 'registry' ? `${routes.owner}/requests` : `${routes.owner}/requests/preliminary`);
  };

  const handleClose = () => navigate(routes.owner);
  const handleBack = () => navigate(routes.owner);

  const title = activeTab === 'preliminary' ? 'Предварительные ответы' : 'Заявки в работе';
  const subtitle =
    activeTab === 'preliminary'
      ? 'Реестр предварительных ответов, источники и статусы по услугам'
      : 'Реестр, статусы, уточнения, история и коммуникация';

  return (
    <>
      <div className="owner-uat-overlay" role="dialog" aria-modal="true" aria-label={title}>
        <div className="owner-uat-overlay-shell">
          <header className="owner-uat-overlay-top">
            <div className="owner-uat-brandmark" aria-hidden="true">
              <img src={logo} alt="" />
            </div>
            <div className="owner-uat-title">
              <small>Владелец системы · Zertteu Academy</small>
              <h1>{title}</h1>
              <p>{subtitle}</p>
              <nav className="owner-uat-overlay-nav" aria-label="Разделы модуля заявок">
                <button
                  type="button"
                  className={activeTab === 'registry' ? 'is-active' : ''}
                  onClick={() => changeTab('registry')}
                >
                  Реестр{reqCount ? ` · ${reqCount}` : ''}
                </button>
                <button
                  type="button"
                  className={activeTab === 'preliminary' ? 'is-active' : ''}
                  onClick={() => changeTab('preliminary')}
                >
                  Предварительные ответы{preCount ? ` · ${preCount}` : ''}
                </button>
              </nav>
            </div>
            <div className="owner-uat-overlay-actions">
              <button type="button" className="owner-uat-btn" onClick={handleBack}>
                ← Назад
              </button>
              <button type="button" className="owner-uat-btn" onClick={handleClose}>
                Закрыть ×
              </button>
            </div>
          </header>

          <div className="owner-uat-overlay-body">
            {activeTab === 'registry' ? (
              <WorkRequestsRegistry roleContext={roleContext} isStandalone={false} />
            ) : (
              <div className="owner-uat-root">
                <PreliminaryRegistryView
                  roleContext={roleContext}
                  onOpenRequest={(code) => setSelectedReqCode(code)}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <RequestPassportModal
        isOpen={!!selectedReqCode}
        requestIdOrCode={selectedReqCode}
        onClose={() => setSelectedReqCode(null)}
        roleContext={roleContext}
      />
    </>
  );
};
